import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { GripVertical, Plus, Trash2, Loader2, RotateCcw } from "lucide-react";
import Marquee from "react-fast-marquee";
import { toast } from "sonner";

const FALLBACK = ["100% NATUREL", "NUTRITION ESSENTIELLE", "ÉNERGIE VIBRANTE", "BIEN-ÊTRE", "ÉDITION LIMITÉE"];

/**
 * Editor for the editorial marquee items (the scrolling band on the home).
 * Open to admin AND assistant — backed by /api/site/marquee.
 */
export default function MarqueeEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/site/marquee");
      setItems(Array.isArray(r.data?.items) ? r.data.items : FALLBACK);
    } catch {
      setItems(FALLBACK);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const setAt = (idx, v) => setItems((arr) => arr.map((s, i) => (i === idx ? v : s)));
  const removeAt = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setItems((arr) => {
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      const copy = [...arr];
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  };
  const add = () => setItems((arr) => [...arr, ""]);

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = items.map(s => (s || "").trim()).filter(Boolean);
    if (cleaned.length < 2) return toast.error("Au moins 2 libellés sont requis");
    if (cleaned.length > 12) return toast.error("Maximum 12 libellés");
    setSaving(true);
    try {
      await api.put("/site/marquee", { items: cleaned });
      setItems(cleaned);
      toast.success("Bandeau défilant mis à jour");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const restoreDefaults = () => {
    if (!window.confirm("Restaurer les libellés par défaut ?")) return;
    setItems(FALLBACK);
  };

  if (loading) {
    return (
      <div data-testid="marquee-editor-loading" className="text-sm text-[#4B634B] py-12 text-center">
        Chargement…
      </div>
    );
  }

  const previewItems = items.map(s => (s || "").trim()).filter(Boolean);

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]" data-testid="marquee-editor">
      <form onSubmit={submit} className="space-y-3">
        <Label>Libellés du bandeau défilant (max 12, max 60 caractères chacun)</Label>
        <div className="space-y-2">
          {items.map((s, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`marquee-row-${idx}`}>
              <div className="flex flex-col">
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0}
                  title="Monter"
                  data-testid={`marquee-up-${idx}`}
                  className="text-[#9CB39C] hover:text-[#1A2E1A] disabled:opacity-30 leading-none px-1"
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, +1)}
                  disabled={idx === items.length - 1}
                  title="Descendre"
                  data-testid={`marquee-down-${idx}`}
                  className="text-[#9CB39C] hover:text-[#1A2E1A] disabled:opacity-30 leading-none px-1"
                >
                  ▼
                </button>
              </div>
              <GripVertical className="w-4 h-4 text-[#9CB39C]" aria-hidden="true" />
              <Input
                value={s}
                onChange={(e) => setAt(idx, e.target.value.toUpperCase())}
                placeholder="ex : LIVRAISON OFFERTE"
                maxLength={60}
                data-testid={`marquee-input-${idx}`}
                className="font-bold tracking-wider uppercase"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeAt(idx)}
                disabled={items.length <= 2}
                title="Supprimer"
                data-testid={`marquee-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={add}
              disabled={items.length >= 12}
              data-testid="marquee-add"
            >
              <Plus className="w-4 h-4 mr-1" /> Ajouter un libellé
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={restoreDefaults}
              data-testid="marquee-restore"
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Valeurs par défaut
            </Button>
          </div>
          <Button
            type="submit"
            className="bg-[#7AC143] hover:bg-[#67BC2A] text-white"
            disabled={saving}
            data-testid="marquee-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enregistrer
          </Button>
        </div>
        <p className="text-xs text-[#4B634B]">Astuce : utilisez ce bandeau pour des messages courts et marquants. Idéal pour pousser un événement saisonnier (« BLACK FRIDAY · -20% · LIVRAISON OFFERTE »).</p>
      </form>

      {/* Live preview */}
      <div>
        <Label className="block mb-3">Aperçu</Label>
        <div className="bg-[#1A2E1A] text-white py-4 border-y border-[#7AC143]/20 rounded-md overflow-hidden" data-testid="marquee-preview">
          {previewItems.length >= 2 ? (
            <Marquee speed={40} gradient={false}>
              {previewItems.concat(previewItems).map((it, i) => (
                <span key={i} className="mx-12 text-xs tracking-[0.3em] font-bold uppercase">
                  <span className="text-[#7AC143] mr-12">✦</span>{it}
                </span>
              ))}
            </Marquee>
          ) : (
            <div className="text-center text-xs text-[#9CB39C]">Au moins 2 libellés requis pour l'aperçu</div>
          )}
        </div>
      </div>
    </div>
  );
}
